import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { LuSearch, LuX } from "react-icons/lu";
import { toast } from "react-toastify";
import {
  extractApiMessage,
  fetchEligibleExhibitionLogisticsProviders,
  fetchExhibitionLogisticsProviderAssignments,
  syncExhibitionLogisticsProviderAssignments,
} from "../../services/exhibitionLogisticsRfqProviderAssignment.service";
import "../../assets/styles/super_admin_access.scss";
import "../../assets/styles/business_relocation_rfq_provider_assign.scss";

const DEBOUNCE_MS = 350;

function formatStatus(status) {
  const s = String(status || "Active").trim();
  if (!s) return "Active";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ExhibitionLogisticsAssignProvidersModal({
  open,
  rfq,
  onClose,
  onSaved,
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const rfqId = rfq?.id;
  const rfqLabel = rfq?.rfqId || rfq?.rfqReference || "RFQ";

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const loadEligible = useCallback(async () => {
    if (!rfqId) return;
    setLoadingProviders(true);
    try {
      const data = await fetchEligibleExhibitionLogisticsProviders({
        rfqId,
        search: debouncedQuery,
        page: 1,
        pageSize: 50,
      });
      setProviders(Array.isArray(data.providers) ? data.providers : []);
    } catch (err) {
      toast.error(extractApiMessage(err, "Could not load providers."));
      setProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  }, [debouncedQuery, rfqId]);

  const loadAssignments = useCallback(async () => {
    if (!rfqId) return;
    setLoadingAssignments(true);
    try {
      const data = await fetchExhibitionLogisticsProviderAssignments(rfqId);
      const ids = (data.providers || []).map((p) => p.companyId);
      setSelected(new Set(ids));
    } catch (err) {
      toast.error(extractApiMessage(err, "Could not load current assignments."));
      setSelected(new Set());
    } finally {
      setLoadingAssignments(false);
    }
  }, [rfqId]);

  useEffect(() => {
    if (!open || !rfqId) return;
    setQuery("");
    setDebouncedQuery("");
    void loadAssignments();
  }, [open, rfqId, loadAssignments]);

  useEffect(() => {
    if (!open || !rfqId) return;
    void loadEligible();
  }, [open, rfqId, loadEligible]);

  const allVisibleIds = useMemo(
    () => providers.map((p) => p.companyId).filter((id) => id != null),
    [providers],
  );

  const allSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));

  const toggleOne = (companyId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allVisibleIds.forEach((id) => next.delete(id));
      } else {
        allVisibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleAssign = async () => {
    if (!rfqId) return;
    setSaving(true);
    try {
      const result = await syncExhibitionLogisticsProviderAssignments(rfqId, [...selected]);
      const count = result?.assignedProviderCount ?? selected.size;
      toast.success(`Assigned ${count} provider${count === 1 ? "" : "s"} successfully.`);
      onSaved?.(result);
      onClose?.();
    } catch (err) {
      toast.error(extractApiMessage(err, "Could not save assignments."));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="sa-dispute-modal-overlay brpq-assign-overlay" role="presentation" onClick={onClose}>
      <div
        className="sa-dispute-modal brpq-assign-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="elpq-assign-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sa-dispute-modal__header">
          <div>
            <h2 id="elpq-assign-title">Assign Logistics Providers</h2>
            <p>{rfqLabel}</p>
          </div>
          <button type="button" className="sa-dispute-modal__close" onClick={onClose} aria-label="Close">
            <LuX aria-hidden />
          </button>
        </header>

        <div className="sa-dispute-modal__body brpq-assign-modal__body">
          <label className="sa-access-search brpq-assign-search">
            <LuSearch aria-hidden />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search company, contact, or email…"
              autoFocus
            />
          </label>

          <div className="brpq-assign-toolbar">
            <label className="brpq-assign-select-all">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              <span>Select all on this page</span>
            </label>
            <span className="brpq-assign-count">{selected.size} selected</span>
          </div>

          <div className="sa-access-table-wrap brpq-assign-table-wrap">
            <table className="sa-access-table brpq-assign-table">
              <thead>
                <tr>
                  <th aria-label="Select" />
                  <th>Company Name</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loadingProviders || loadingAssignments ? (
                  <tr>
                    <td colSpan={6} className="brpq-assign-empty">
                      Loading…
                    </td>
                  </tr>
                ) : providers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="brpq-assign-empty">
                      No eligible Exhibition Logistics providers found.
                    </td>
                  </tr>
                ) : (
                  providers.map((row) => (
                    <tr key={row.companyId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(row.companyId)}
                          onChange={() => toggleOne(row.companyId)}
                          aria-label={`Select ${row.companyName}`}
                        />
                      </td>
                      <td>{row.companyName}</td>
                      <td>{row.contactPerson || "—"}</td>
                      <td>{row.email || "—"}</td>
                      <td>{row.phone || "—"}</td>
                      <td>{formatStatus(row.status)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="brpq-assign-footer">
          <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="sa-access-btn sa-access-btn--primary"
            onClick={() => void handleAssign()}
            disabled={saving || loadingAssignments}
          >
            {saving ? "Assigning…" : `Assign Providers (${selected.size})`}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export function ExhibitionLogisticsAssignedProvidersModal({ open, rfq, providers, loading, onClose }) {
  if (!open) return null;
  const rfqLabel = rfq?.rfqId || rfq?.rfqReference || "RFQ";

  return createPortal(
    <div className="sa-dispute-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="sa-dispute-modal brpq-assign-modal brpq-assign-modal--view"
        role="dialog"
        aria-modal="true"
        aria-labelledby="elpq-view-assign-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sa-dispute-modal__header">
          <div>
            <h2 id="elpq-view-assign-title">Assigned Providers</h2>
            <p>{rfqLabel}</p>
          </div>
          <button type="button" className="sa-dispute-modal__close" onClick={onClose} aria-label="Close">
            <LuX aria-hidden />
          </button>
        </header>
        <div className="sa-dispute-modal__body">
          {loading ? (
            <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">Loading…</p>
          ) : !providers?.length ? (
            <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
              No providers assigned yet.
            </p>
          ) : (
            <div className="sa-access-table-wrap">
              <table className="sa-access-table brpq-assign-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map((p) => (
                    <tr key={p.companyId || p.id}>
                      <td>{p.companyName}</td>
                      <td>{p.contactPerson || "—"}</td>
                      <td>{p.email || "—"}</td>
                      <td>{p.phone || "—"}</td>
                      <td>{formatStatus(p.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
